import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Balloons from "../../../public/images/balloons.png";

import SurveyComponent from "@assessment/components/molecules/AssessmentSurvey/surveyComponent";
import { AppMetaDataSliceActions } from "@lxp-app/store/slices/appMetaDataSlice";
import {
  useAppDispatch,
  RootState,
  useAppSelector
} from "@assessment/store/store";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import ShowResultOnSubmit from "./_components/showResult/showResultOnSubmit";
import orgBasedFeatureNameMapping from "@assessment/utils/orgBasedFeatureName";
import { useSelector } from "react-redux";
import Button from "@lxp-app/components/atoms/button";
import SmallAppLoader from "@lxp-app/components/atoms/app-loader/smallAppLoader";
import { useLazyGetAssessmentResultStatusQuery } from "@assessment/store/slices/api/assessmentApiSlice";
import WhatsNext from "./_components/showResult/whatsNext";
import { CertificationResultScreen } from "./_components/certificationResultScreen";

const AssessmentFeedbackPage = () => {
  const { t } = useTranslation();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [surveyRes, setSurveyRes] = useState("");
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();
  const location = useLocation();
  const user = useSelector((state: RootState) => state.UserInfoSlice.profile);
  const ongoingId = params.get("ongoingId");
  const feedbackAllowed = location.state?.feedbackAllowed;
  const feedbackId = location.state?.feedbackId;
  const showScore = location.state?.showScore;
  const showPercentage = location.state?.showPercentage;
  const obtainedMarks = location.state?.marksObtained;
  const totalMarks = location.state?.totalMarks;
  const initialResultStatus = location.state?.resultStatus;
  const resultStatus = useRef<"in-progress" | "completed" | "error">(
    initialResultStatus
  );
  const [showResults, setShowResults] = useState<boolean>(false);
  const { scheduleId: parentId } = useParams<{ scheduleId: string }>();
  const navigate = useNavigate();
  const orgBasedFeatureName =
    orgBasedFeatureNameMapping[user?.org || "default"] ||
    orgBasedFeatureNameMapping["default"];
  const initialResults = {
    obtainedMarks: Number(obtainedMarks),
    totalMarks: Number(totalMarks)
  };
  const [results, setResults] = useState(initialResults);
  const [fetchResultStatus] = useLazyGetAssessmentResultStatusQuery();
  const delayTimerRef = useRef(20);
  const intervalRef = useRef<any>(null);
  const pollingStartedRef = useRef(false);
  const [resultStatusState, setResultStatusState] = useState<
    "in-progress" | "completed" | "error"
  >(initialResultStatus);
  const [isCertificate, setIsCertificate] = useState(false);
  const { scheduleId } = useParams();
  const percentage = (results?.obtainedMarks / results?.totalMarks) * 100;
  const [skills, setSkills] = useState<string[] | null>(null);
  const [accessDetails, setAccessDetails] = useState<any>(null);
  const [certificationDetails, setCertificationDetails] = useState<any>(null);
  const [assessmentTitle, setAssessmentTitle] = useState<string | null>(null);
  const assessmentData = useAppSelector(
    (state) => state.AssessmentSlice.assessmentData
  );
  const passed =
    percentage >= (Number(localStorage.getItem("passPercentage")) || 0);
  const [redirectSeconds, setRedirectSeconds] = useState<number>(10);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (
      resultStatusState === "completed" &&
      redirectSeconds > 0 &&
      isCertificate &&
      (showPercentage || showScore)
    ) {
      timerRef.current = setTimeout(() => {
        setRedirectSeconds((prev) => prev - 1);
      }, 1000);
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    }
    if (
      redirectSeconds === 0 &&
      resultStatusState === "completed" &&
      isCertificate &&
      (showPercentage || showScore)
    ) {
      setShowResults(true);
    }
  }, [redirectSeconds, resultStatusState, isCertificate]);

  useEffect(() => {
    if (location.state == null) {
      navigate(`/assessment/${parentId}`);
    }
    dispatch(AssessmentSliceActions.resetState());

    if (initialResultStatus === "completed" && (showPercentage || showScore)) {
      fetchResultStatus(ongoingId!).then((res: any) => {
        if (res.data.data.resultStatus === "completed") {
          setSkills(res.data.data?.skills);
          setAccessDetails(res.data.data?.access);
          setCertificationDetails(res.data.data?.certificateDetails);
          if (res.data.data?.certificateDetails !== null) {
            setIsCertificate(true);
          }
          setAssessmentTitle(res.data.data?.title);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (assessmentData && assessmentData?.passPercentage)
      localStorage.setItem(
        "passPercentage",
        String(assessmentData?.passPercentage)
      );
  }, [assessmentData]);

  useEffect(() => {
    if (assessmentData && assessmentData?.passPercentage)
      localStorage.setItem(
        "passPercentage",
        String(assessmentData?.passPercentage)
      );
  }, [assessmentData]);

  useEffect(() => {
    // Only start polling if status is in-progress and polling hasn't started
    if (
      !resultStatus.current ||
      resultStatus.current === "completed" ||
      resultStatus.current === "error" ||
      (!showPercentage && !showScore)
    )
      return;

    if (resultStatus.current === "in-progress" && !pollingStartedRef.current) {
      pollingStartedRef.current = true;

      const timeoutId = setTimeout(() => {
        intervalRef.current = setInterval(() => {
          //if result is still in progress after 60 seconds, set it to error to avoid infinite polling
          if (delayTimerRef.current >= 60) {
            resultStatus.current = "error";
            setResultStatusState("error");
            clearInterval(intervalRef.current);
            pollingStartedRef.current = false;
            return;
          }

          // Call API to check result status
          fetchResultStatus(ongoingId!)
            .then((res: any) => {
              // If completed, update status and results
              if (res.data.data.resultStatus === "completed") {
                resultStatus.current = "completed";
                setResultStatusState("completed");
                setResults({
                  obtainedMarks: res.data.data.obtainedMarks,
                  totalMarks: res.data.data.totalMarks
                });
                setSkills(res.data.data?.skills);
                setAccessDetails(res.data.data?.access);
                setCertificationDetails(res.data.data?.certificateDetails);
                if (res.data.data?.certificateDetails !== null) {
                  setIsCertificate(true);
                }
                setAssessmentTitle(res.data.data?.certificateDetails?.title);
                clearInterval(intervalRef.current);
                pollingStartedRef.current = false;
                return;
              } else {
                // If still in progress, increment delay timer
                delayTimerRef.current = delayTimerRef.current + 3;
              }
            })
            .catch((err) => {
              // On error, set status to error and stop polling
              resultStatus.current = "error";
              setResultStatusState("error");
              clearInterval(intervalRef.current);
              pollingStartedRef.current = false;
            });
        }, 3000); // Poll every 3 seconds
      }, 10 * 1000); // Initial delay of 10 seconds before starting to poll

      // Cleanup timeout if effect is cleaned up before timeout triggers
      return () => {
        clearTimeout(timeoutId);
        clearInterval(intervalRef.current);
        delayTimerRef.current = 20;
        pollingStartedRef.current = false;
      };
    }

    // Cleanup if resultStatus changes to completed or error
    return () => {
      clearInterval(intervalRef.current);
      delayTimerRef.current = 20;
      pollingStartedRef.current = false;
    };
  }, [resultStatus, ongoingId, fetchResultStatus]);

  useEffect(() => {
    if (AppMetaDataSliceActions) {
      dispatch(AppMetaDataSliceActions.setViewBreadcrumb(false));
    }

    return () => {
      if (AppMetaDataSliceActions) {
        dispatch(AppMetaDataSliceActions.setViewBreadcrumb(true));
      }
    };
  }, []);

  useEffect(() => {
    const isSubmitted = localStorage.getItem("feedbackSubmitted") === "true";
    setAlreadySubmitted(isSubmitted);
  }, []);

  const surveyResFn = useCallback((data: any) => {
    data.length > 0 ? setSurveyRes("err") : setSurveyRes("success");
    setAlreadySubmitted(true);
  }, []);

  const assessmentLoader = () => {
    return (
      <div>
        <div className="h-40 w-full bg-gray-300 rounded-xl animate-pulse my-5"></div>
        <div className="h-40 w-full bg-gray-300 rounded-xl animate-pulse my-5"></div>
        <div className="h-40 w-full bg-gray-300 rounded-xl animate-pulse my-5"></div>
        <div className="h-40 w-full bg-gray-300 rounded-xl animate-pulse my-5"></div>
      </div>
    );
  };

  return (
    <div className="flex items-start justify-center min-h-screen py-10 bg-profile-bkg px-6">
      <div className="px-5 md:px-20 py-8 bg-bkg rounded-3xl 2xl:max-w-[1400px] max-w-full">
        {/* Top */}
        <div className="flex flex-col items-center justify-center text-center">
          {isCertificate && scheduleId && showResults ? (
            <CertificationResultScreen
              passed={passed}
              scheduleId={scheduleId}
              title={assessmentTitle}
            />
          ) : (
            <>
              <img src={Balloons} alt="Balloons" className="h-44" />
              <h1 className="text-2xl lg:text-[32px] leading-10 font-semibold text-secondary mt-10 mb-5">
                {t("assessment.feedback.submittedTitle", {
                  featureName: orgBasedFeatureName.toLowerCase()
                })}
              </h1>

              <p className="text-lg font-normal leading-6 md:text-xl text-secondary/50">
                {t("assessment.feedback.submittedDescription", {
                  featureName: orgBasedFeatureName.toLowerCase()
                })}
              </p>
            </>
          )}

          {initialResultStatus &&
            initialResultStatus !== "completed" &&
            !showResults &&
            (showPercentage || showScore) && (
              <div className="mt-8 mb-3">
                {resultStatusState === "in-progress" && (
                  <>
                    <SmallAppLoader />
                    <p className="text-xl mt-3">
                      {t("assessment.feedback.resultInProgress")}
                    </p>
                  </>
                )}

                {resultStatusState === "completed" && (
                  <p className="text-xl">
                    {t("assessment.feedback.resultReady")}
                  </p>
                )}

                {resultStatusState === "error" && (
                  <p className="text-red-500 text-xl">
                    {t("assessment.feedback.resultDelayed")}
                  </p>
                )}

                <Button
                  label={t("assessment.feedback.showResult")}
                  onClick={() => setShowResults(true)}
                  disable={
                    resultStatusState === "in-progress" ||
                    resultStatusState === "error"
                  }
                  variant="contained"
                  buttonColor={"primary"}
                  className="mt-5"
                />
                {resultStatusState === "completed" && isCertificate && (
                  <p className="text-sm mt-4">
                    {t("assessment.feedback.redirectingToResult", {
                      seconds: redirectSeconds
                    })}
                  </p>
                )}
              </div>
            )}

          <div className="mt-16 w-full flex justify-center">
            {(showScore || showPercentage) &&
              (initialResultStatus === "completed" || showResults) && (
                <div className="w-[60vw] xl:w-[34vw] md:max-w-2xl">
                  <ShowResultOnSubmit
                    data={results}
                    showScore={showScore}
                    showPercentage={showPercentage}
                    orgBasedFeatureName={orgBasedFeatureName}
                    isCertificate={isCertificate}
                  />
                </div>
              )}
          </div>
        </div>

        {scheduleId &&
          isCertificate &&
          skills &&
          certificationDetails &&
          accessDetails &&
          showResults && (
            <WhatsNext
              passed={passed}
              scheduleId={scheduleId}
              skills={skills}
              certificationDetails={certificationDetails}
              accessDetails={accessDetails}
            />
          )}

        {/* Form */}
        {!alreadySubmitted && feedbackAllowed && feedbackId && (
          <form className="max-w-5xl pt-10 mx-auto mt-10 border-t md:px-20 border-border">
            {!(surveyRes.length > 0) && (
              <div className="flex flex-col mb-10 space-y-3 text-center">
                <h2 className="text-2xl font-semibold leading-7 text-secondary">
                  {t("assessment.feedback.shareFeedbackTitle")}
                </h2>
                <p className="text-base font-medium leading-5 text-secondary/50">
                  {t("assessment.feedback.shareFeedbackDescription")}
                </p>
              </div>
            )}
            {feedbackId &&
            feedbackId?.length > 0 &&
            parentId &&
            parentId?.length > 0 ? (
              <SurveyComponent
                surveyId={feedbackId}
                surveyParentId={parentId}
                setSubmitLoading={setSubmitLoading}
                setRes={surveyResFn}
                ongoingId={ongoingId}
              />
            ) : (
              assessmentLoader()
            )}

            {submitLoading && assessmentLoader()}
            {surveyRes.length > 0 ? (
              surveyRes === "err" ? (
                <span className="text-primary text-3xl flex justify-center">
                  {t("assessment.feedback.errorSubmittingFeedback")}
                </span>
              ) : (
                <>
                  <div className="text-xl font-semibold w-full flex justify-center">
                    {t("assessment.feedback.feedbackSubmittedTitle")}
                  </div>
                  <p className="text-xs font-normal md:text-base text-secondary/50 mt-2 w-full flex justify-center">
                    {t("assessment.feedback.feedbackSubmittedDescription", {
                      featureName: orgBasedFeatureName.toLowerCase()
                    })}
                  </p>
                </>
              )
            ) : (
              ""
            )}
          </form>
        )}
        {alreadySubmitted && (
          <>
            <div className="mt-8 text-xl font-semibold w-full flex justify-center">
              {t("assessment.feedback.feedbackAlreadySubmittedTitle")}
            </div>
            <p className="text-xs font-normal md:text-base text-secondary/50 mt-2 w-full flex justify-center">
              {t("assessment.feedback.feedbackAlreadySubmittedDescription", {
                featureName: orgBasedFeatureName.toLowerCase()
              })}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AssessmentFeedbackPage;
